import { useLocation } from '../context/LocationContext';
import { useEffect, useMemo } from 'react';
import { usePaymentMethodsStore } from '../context/PaymentMethodStore';
import { PaymentState } from '../../generated-api';
import { useBusyTerminalPolling } from './useBusyTerminalPolling';
import { useQueryActivePayments } from './useQueryActivePayments';
import {
  TerminalStatusResponseLatestState as TerminalState,
  useGetTerminals,
} from '@ordr-as/payment-service-types/epos';

export const useTerminalsPolling = (terminalIds: string[]) => {
  const { location } = useLocation();
  const setTerminalLatestStates = usePaymentMethodsStore((s) => s.actions.setTerminalLatestStates);
  const terminalLatestStates = usePaymentMethodsStore((s) => s.terminalLatestStates);
  const { data: activePayments } = useQueryActivePayments(location.locationId);

  const { data } = useGetTerminals(
    location.id,
    { ids: terminalIds },
    {
      query: {
        refetchInterval: 15000,
        refetchOnMount: true,
        enabled: terminalIds.length > 0,
      },
    }
  );

  // Derive busy terminal IDs from:
  // 1. Terminals with active payments (non-succeeded, non-failed)
  // 2. Terminals where API reports BUSY state
  const busyTerminalIds = useMemo(() => {
    const terminalsWithActivePayment = new Set(
      activePayments
        ?.filter((ap) => ap.paymentState !== PaymentState.SUCCEEDED && ap.paymentState !== PaymentState.FAILED)
        .map((ap) => ap.paymentMethod.id) ?? []
    );

    return terminalIds.filter(
      (id) => terminalsWithActivePayment.has(id) || terminalLatestStates[id] === TerminalState.BUSY
    );
  }, [terminalIds, terminalLatestStates, activePayments]);

  // Fast polling for busy terminals (3s interval)
  useBusyTerminalPolling(busyTerminalIds);

  // Update store with latest states from API
  useEffect(() => {
    if (!data) return;

    const newStates: Record<string, TerminalState> = {};
    for (const terminal of data) {
      newStates[terminal.pspTerminalId] = terminal.latestState;
    }

    setTerminalLatestStates(newStates);
  }, [data, setTerminalLatestStates]);

  return data;
};
